import { parseRelKey } from './familyMember.utils'
import { NO_RELATION_KEY } from './saveRelationships'

/**
 * Transform relationships from form format to map format
 *
 * @param {Array} relationships - Array of relationship objects from form
 * @param {string} mainPersonId - ID of the main person
 * @returns {Array} Array of relationships in map format
 */
export const transformRelationshipsForMap = (relationships, mainPersonId) => {
  if (!relationships || !mainPersonId) return []

  return relationships
    .filter((rel) => rel && (rel.slsRelationshipId || rel.formId) && rel.personId)
    .map((rel) => ({
      id: rel.slsRelationshipId || rel.formId,
      firstPersonId: mainPersonId,
      secondPersonId: rel.personId,
      relationshipType: rel.relationshipType,
      relationshipDirection: rel.relationshipDirection,
    }))
}

/**
 * Get or create relationship ID from existing relationship or field
 * Uses existing relationship ID if found to maintain consistency
 *
 * @param {Object|null} existingRelationship - Existing relationship from map
 * @param {Object} field - Form field object
 * @returns {string} Relationship ID to use
 */
export const getRelationshipId = (existingRelationship, field) => {
  return existingRelationship?.id || field?.slsRelationshipId || field?.formId
}

/**
 * Create a relationship object for the map
 *
 * @param {Object} params
 * @param {string} params.relationshipId - ID of the relationship
 * @param {string} params.firstPersonId - ID of the first person
 * @param {string} params.secondPersonId - ID of the second person
 * @param {string} params.relationshipType - Type of relationship
 * @param {number} params.relationshipDirection - Direction of relationship
 * @returns {Object} Relationship object for the map
 */
export const createRelationshipObject = ({
  relationshipId,
  firstPersonId,
  secondPersonId,
  relationshipType,
  relationshipDirection,
}) => {
  return {
    id: relationshipId,
    firstPersonId,
    secondPersonId,
    relationshipType,
    relationshipDirection,
  }
}

/**
 * Update relationship in map when relationship type changes
 *
 * @param {Object} params
 * @param {import('../hooks/useRelationshipsMap').RelationshipsMapApi} params.relationshipsMapApi - The relationships map API
 * @param {Object} params.suggestion - The selected relationship suggestion
 * @param {Object} params.field - The form field object
 * @param {string} params.firstPersonId - ID of the first person
 * @param {string} params.secondPersonId - ID of the second person
 * @returns {boolean} True if updated, false if skipped
 */
export const updateRelationshipType = ({ relationshipsMapApi, suggestion, field, firstPersonId, secondPersonId }) => {
  // Validate inputs
  if (!relationshipsMapApi || !firstPersonId || !secondPersonId || !suggestion?.key) {
    return false
  }

  // Parse the relationship type from suggestion
  const { relKey, order } = parseRelKey(suggestion.key)

  // Check if relationship exists
  const existingRelationship = relationshipsMapApi.findRelationship(firstPersonId, secondPersonId)

  // Get relationship ID (prefer existing to maintain consistency)
  const relationshipId = getRelationshipId(existingRelationship, field)

  // Handle "No Relation" cases
  if (relKey === NO_RELATION_KEY) {
    if (existingRelationship) {
      // If selecting "No Relation" and relationship exists, remove it
      relationshipsMapApi.removeRelationship(relationshipId)
      return true
    }
    // If selecting "No Relation" and no existing relationship, nothing to do
    return true
  }

  // Add or update relationship
  relationshipsMapApi.addOrUpdateRelationship(
    createRelationshipObject({
      relationshipId,
      firstPersonId,
      secondPersonId,
      relationshipType: relKey,
      relationshipDirection: order,
    })
  )

  return true
}

/**
 * Get the relationship direction from the perspective of a specific person
 * If the person is the firstPerson in the relationship, return the stored direction
 * If the person is the secondPerson, invert the direction
 *
 * @param {Object} relationship - The relationship object
 * @param {string} personId - ID of the person whose perspective we want
 * @returns {string} The direction from this person's perspective ('FIRST' or 'SECOND')
 */
export const getRelationshipDirectionForPerson = (relationship, personId) => {
  if (!relationship || !personId) return null

  const { firstPersonId, secondPersonId, relationshipDirection } = relationship

  // If this person is the first person, use the stored direction
  if (personId === firstPersonId) {
    return relationshipDirection
  }

  // If this person is the second person, invert the direction
  if (personId === secondPersonId) {
    if (relationshipDirection === 'FIRST') return 'SECOND'
    if (relationshipDirection === 'SECOND') return 'FIRST'
    return relationshipDirection
  }

  return relationshipDirection
}

/**
 * Enrich relationship fields with data from the relationships map
 * Used to preselect relationship types in forms based on existing data
 *
 * @param {Object} params
 * @param {Array} params.fields - Array of relationship fields
 * @param {Object} params.relationshipsMapApi - The relationships map API
 * @param {string} params.personIdToReview - ID of the person being reviewed
 * @param {Array} params.relationshipSuggestions - Array of relationship type suggestions
 * @returns {Array} Enriched relationship fields with preselected values
 */
export const enrichRelationshipFields = ({
  fields,
  relationshipsMapApi,
  personIdToReview,
  relationshipSuggestions,
}) => {
  if (!relationshipsMapApi || !personIdToReview || !fields || !relationshipSuggestions) {
    return fields || []
  }

  const processedFields = fields.map((field) => {
    const existingRelationship = relationshipsMapApi.findRelationship(personIdToReview, field.personId)

    if (existingRelationship) {
      // Get the relationship direction from the OTHER person's perspective (field.personId)
      // Because the dropdown shows what the other person is TO the personIdToReview
      const directionForPerson = getRelationshipDirectionForPerson(existingRelationship, field.personId)

      // Build the relationship key (e.g., "PARENT_CHILD_FIRST", "PARENT_CHILD_SECOND")
      const relKey = `${existingRelationship.relationshipType}_${directionForPerson || ''}`

      // Find the matching suggestion to get the label
      let matchingSuggestion = relationshipSuggestions.find((s) => s.key === relKey)

      // Fallback: If _SECOND key is not found, try _FIRST as some relationship types
      // don't need a _SECOND variant (symmetrical relationships, i.e., SIBLING)
      if (!matchingSuggestion && directionForPerson === 'SECOND') {
        const fallbackKey = `${existingRelationship.relationshipType}_FIRST`
        matchingSuggestion = relationshipSuggestions.find((s) => s.key === fallbackKey)
      }

      return {
        ...field,
        relationshipType: existingRelationship.relationshipType,
        relationshipDirection: directionForPerson,
        relationship: matchingSuggestion || field.relationship,
      }
    }

    return field
  })

  return processedFields
}
